import { useState } from "react"
import { Box, Flex, Heading, Text, Spinner } from "@chakra-ui/react"
import { neumorph, detailsBtnSx } from "./neumorph"

/**
 * Reusable bento card wrapper for the Insights dashboard.
 *
 * Props:
 *   title       — short heading
 *   subtitle    — optional one-line description
 *   loading     — boolean, shows a spinner over the body when true
 *   onDetails   — optional click handler. If present, a "Details" button
 *                 appears on hover in the top-right.
 *   minH        — optional CSS min-height (default '220px')
 *   gridSpan    — optional { base, md, lg } colSpan for SimpleGrid layouts
 *   children    — card body content (mini chart, list, KPI, etc.)
 */
const BentoCard = ({
  title,
  subtitle,
  loading = false,
  onDetails,
  minH = "220px",
  gridSpan,
  children,
}) => {
  const [hovered, setHovered] = useState(false)

  return (
    <Box
      sx={{
        ...neumorph.raised,
        ...(hovered ? neumorph.raisedHover : {}),
        position: "relative",
        p: 4,
        minH,
        display: "flex",
        flexDirection: "column",
        ...(gridSpan ? { gridColumn: `span ${gridSpan}` } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {onDetails && (
        <Box
          as="button"
          sx={{
            ...detailsBtnSx,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? "auto" : "none",
          }}
          onClick={(e) => {
            e.stopPropagation()
            onDetails()
          }}
        >
          Details →
        </Box>
      )}

      <Box mb={2} pr={onDetails ? 16 : 0}>
        <Heading size="xs" color={neumorph.textPrimary} mb={subtitle ? 0.5 : 0}>
          {title}
        </Heading>
        {subtitle && (
          <Text fontSize="2xs" color={neumorph.textMuted}>
            {subtitle}
          </Text>
        )}
      </Box>

      <Box flex="1" position="relative" minH={0}>
        {loading ? (
          <Flex justify="center" align="center" h="100%" minH="120px">
            <Spinner size="md" color={neumorph.accentTeal} />
          </Flex>
        ) : (
          children
        )}
      </Box>
    </Box>
  )
}

export default BentoCard
